import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, throwIfNotOk } from '../lib/api';
import { useAuth } from '../lib/authContext';
import { ProfileLockedCard } from '../components/ProfileLockedCard';
import { formatDate } from '../lib/format';
import { todayISO, formatDeliveryWindow, formatDayMonthYear } from '../lib/dates';
import { DateField } from '../components/shared/DateField';
import type { Opportunity } from '@siliconx/shared/dto/direct-offer';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import { qk } from '../lib/queryKeys';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-171 — seller "opportunities" feed: OPEN direct offers (SIL-170's
 * GET /opportunities) the caller's own inventory could fill. A row's Accept
 * action lets the seller pick specific asset units (assetIds) to claim
 * against the direct offer, mirroring accept_direct_offer()'s per-unit
 * shape (SIL-170's POST /:id/accept).
 *
 * UX decision (unit-picking): the API has no single "eligible units for
 * this direct offer" endpoint, so the accept dialog narrows the seller's own
 * GET /assets list by condition band (exact match, server-enforced anyway)
 * and model name (ilike, derived from a one-off GET /skus/:skuId lookup),
 * then further narrows by region using GET /locations to map the direct offer's
 * region to the seller's matching data centers (asset.dataCenterId). Any
 * unit that still fails a deeper eligibility check (lifecycle state,
 * already-listed, etc.) surfaces via POST /accept's structured error codes
 * rather than being filtered out client-side twice.
 */
// SIL-1108: `orgFeature: 'marketplaceOps'` (customerNav.ts) is
// business-only — gated here so a direct hit doesn't fire this page's
// mount-time fetch before the root `OrgFeatureGate`'s (__root.tsx) redirect
// lands.
export const Route = createFileRoute('/opportunities')({
  beforeLoad: () => requireOrgFeature('marketplaceOps'),
  component: OpportunitiesPage,
});

interface OpportunitiesResponse {
  data: Opportunity[];
  optedOut: boolean;
  newCount: number;
  meta: { nextCursor: string | null; limit: number };
}

interface SkuDetail {
  id: string;
  make: string | null;
  model: string | null;
}

interface AssetOption {
  id: string;
  serialNumber: string | null;
  modelName: string | null;
  condition: string | null;
  dataCenterId: string | null;
  dataCenterName: string | null;
}

interface AssetsResponse {
  data: AssetOption[];
}

interface LocationOption {
  id: string;
  regionId: string;
}

/** Maps accept_direct_offer()'s structured error codes to a friendly message. */
const ACCEPT_ERROR_MESSAGES: Record<string, string> = {
  insufficient_remaining_quantity: 'That claims more units than remain open on this direct offer.',
  below_floor: 'Your price does not clear this buyer’s floor.',
  opted_out: 'Your org has opted out of unsolicited direct offers.',
  invalid_transition: 'This direct offer is no longer open.',
  units_not_eligible: 'One or more selected units are not eligible (already listed, wrong lifecycle state, or a mismatched SKU/condition/region).',
  not_found: 'This direct offer (or one of the selected assets) could not be found.',
  // 670: forward-availability accept validation.
  outside_delivery_window: 'That availability date falls outside this buyer’s delivery window.',
  future_date_required: 'This asset is in service — pick a future availability date.',
};

/**
 * SIL-222 — the matching-unit computation (which of the seller's own assets
 * could fill a direct offer) extracted out of AcceptDialog so it can be
 * reused read-only by the "View units" viewer. `selected`/`onToggle`/
 * `remaining` are all optional: selection checkboxes render ONLY when
 * `onToggle` is supplied, so the read-only viewer gets plain rows.
 *
 * All hooks here are unconditional (no early return above them) so this
 * component is safe to use from either consumer regardless of readOnly-ness.
 */
function MatchingUnitsList({
  opportunity,
  selected,
  onToggle,
  remaining,
}: {
  opportunity: Opportunity;
  selected?: Set<string>;
  onToggle?: (id: string) => void;
  remaining?: number;
}) {
  const { data: sku } = useQuery({
    queryKey: qk.skuDetail(opportunity.skuId),
    queryFn: async () => {
      const res = await api.api.client.skus[':skuId'].$get({ param: { skuId: opportunity.skuId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as SkuDetail;
    },
  });

  const { data: locations } = useQuery({
    queryKey: qk.locations(),
    queryFn: async () => {
      const res = await api.api.client.locations.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as LocationOption[];
    },
    staleTime: 5 * 60_000,
  });

  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: qk.assets.forOpportunity(opportunity.conditionBand, sku?.model),
    queryFn: async () => {
      const res = await api.api.client.assets.$get({
        query: { condition: opportunity.conditionBand, limit: '200', ...(sku?.model ? { model: sku.model } : {}) },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as AssetsResponse;
    },
    enabled: !!sku,
  });

  // Region narrowing: which of the seller's own data centers are in the
  // direct offer's region (null region on the direct offer = any region).
  const matchingDataCenterIds = useMemo(() => {
    if (!opportunity.regionId || !locations) return null;
    return new Set(locations.filter((l) => l.regionId === opportunity.regionId).map((l) => l.id));
  }, [opportunity.regionId, locations]);

  const eligibleAssets = useMemo(() => {
    const rows = assets?.data ?? [];
    if (!matchingDataCenterIds) return rows;
    return rows.filter((a) => a.dataCenterId && matchingDataCenterIds.has(a.dataCenterId));
  }, [assets, matchingDataCenterIds]);

  return (
    <>
      <p className="mt-1 text-sm text-muted-foreground">
        {[sku?.make, sku?.model].filter(Boolean).join(' ') || opportunity.skuId.slice(0, 8)} &middot;{' '}
        {conditionLabel(opportunity.conditionBand)} &middot; $
        {opportunity.pricePerUnitUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}/unit
        {remaining != null && (
          <>
            {' '}
            &middot; {remaining} unit{remaining !== 1 ? 's' : ''} remaining
          </>
        )}
      </p>

      <div className="mt-4 max-h-64 overflow-y-auto rounded-md border border-border">
        {assetsLoading && <div className="p-4 text-sm text-muted-foreground">Loading your matching units…</div>}
        {!assetsLoading && eligibleAssets.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">
            No matching units found in your inventory for this SKU/condition/region.
          </div>
        )}
        {eligibleAssets.map((a) => (
          <label
            key={a.id}
            className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0 hover:bg-muted"
          >
            <span className="flex items-center gap-2">
              {onToggle && (
                <input
                  type="checkbox"
                  checked={selected?.has(a.id) ?? false}
                  disabled={!selected?.has(a.id) && (selected?.size ?? 0) >= (remaining ?? 0)}
                  onChange={() => onToggle(a.id)}
                  className="rounded border-border text-brand-600 focus:ring-brand-500"
                />
              )}
              <span className="font-mono text-xs text-foreground">{a.serialNumber ?? a.id.slice(0, 8)}</span>
            </span>
            <span className="text-xs text-muted-foreground">{a.dataCenterName ?? '—'}</span>
          </label>
        ))}
      </div>

      {remaining != null && (
        <p className="mt-2 text-xs text-muted-foreground">
          Selected {selected?.size ?? 0} of {remaining} remaining unit{remaining !== 1 ? 's' : ''}.
        </p>
      )}
    </>
  );
}

function AcceptDialog({ opportunity, onClose }: { opportunity: Opportunity; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const remaining = opportunity.quantityDesired - opportunity.quantityFilled;

  // 670: seller-stated availability date for the claimed units. Defaults to
  // today (= immediate transfer); client-side min/max keep it inside the
  // buyer's delivery window so out-of-window picks can't even be submitted.
  const [availableOn, setAvailableOn] = useState(() => {
    const today = todayISO();
    return opportunity.deliverFrom && opportunity.deliverFrom > today
      ? opportunity.deliverFrom
      : today;
  });
  const windowMin =
    opportunity.deliverFrom && opportunity.deliverFrom > todayISO()
      ? opportunity.deliverFrom
      : todayISO();
  const windowMax = opportunity.deliverBy ?? undefined;
  const outOfWindow =
    availableOn !== '' &&
    (availableOn < windowMin || (windowMax != null && availableOn > windowMax));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < remaining) {
        next.add(id);
      }
      return next;
    });
  }

  const accept = useMutation({
    mutationFn: async () => {
      const res = await api.api.client['direct-offers'][':id'].accept.$post({
        param: { id: opportunity.id },
        json: {
          assetIds: Array.from(selected),
          idempotencyKey: crypto.randomUUID(),
          availableOn: availableOn || undefined,
        },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      toast.success('Direct offer accepted.');
      void queryClient.invalidateQueries({ queryKey: qk.opportunities.all });
      onClose();
    },
    onError: (err: Error & { body?: { error?: { code?: string; message?: string } } }) => {
      const code = err.body?.error?.code;
      toast.error((code && ACCEPT_ERROR_MESSAGES[code]) ?? err.body?.error?.message ?? err.message ?? 'Accept failed.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-w-lg shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">Accept direct offer</h2>

        <MatchingUnitsList opportunity={opportunity} selected={selected} onToggle={toggle} remaining={remaining} />

        <div className="mt-4">
          <DateField
            label="Availability date"
            value={availableOn}
            onChange={setAvailableOn}
            min={windowMin}
            max={windowMax}
            helper={`Buyer's delivery window: ${formatDeliveryWindow(opportunity.deliverFrom, opportunity.deliverBy)}. Today = immediate transfer.`}
            error={
              outOfWindow
                ? `Must be within the buyer's window (${
                    windowMax != null
                      ? `${formatDayMonthYear(windowMin)} – ${formatDayMonthYear(windowMax)}`
                      : `from ${formatDayMonthYear(windowMin)}`
                  }).`
                : null
            }
          />
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={accept.isPending}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={() => accept.mutate()}
            disabled={selected.size === 0 || outOfWindow || !availableOn || accept.isPending}
          >
            {accept.isPending ? 'Accepting…' : `Accept ${selected.size || ''} unit${selected.size === 1 ? '' : 's'}`.trim()}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** Read-only viewer: same matching-unit data as AcceptDialog, no controls. */
function ViewUnitsDialog({ opportunity, onClose }: { opportunity: Opportunity; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="max-w-lg shadow-lg w-full">
        <h2 className="text-base font-semibold text-foreground">Matching units</h2>

        <MatchingUnitsList opportunity={opportunity} />

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>
      </Card>
    </div>
  );
}

function OpportunitiesPage() {
  const { canSell, sellerMissingFields } = useAuth();
  const queryClient = useQueryClient();
  const [acceptTarget, setAcceptTarget] = useState<Opportunity | null>(null);
  const [viewTarget, setViewTarget] = useState<Opportunity | null>(null);

  const oppQuery = useQuery({
    queryKey: qk.opportunities.all,
    queryFn: async () => {
      const res = await api.api.client.opportunities.$get({ query: {} });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as OpportunitiesResponse;
    },
    enabled: canSell,
  });

  const markSeen = useMutation({
    mutationFn: async () => {
      const res = await api.api.client.opportunities['mark-seen'].$post();
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      // Clears the nav badge (AppShell's separate ['client-opportunities-badge'] query).
      void queryClient.invalidateQueries({ queryKey: qk.opportunities.badge() });
    },
  });

  // Clear the nav badge as soon as the tab is opened.
  useEffect(() => {
    if (canSell) markSeen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSell]);

  if (!canSell) {
    return (
      <div className="space-y-6">
        <PageHeader title="Buyer Demand" pinTo="/opportunities" />
        <ProfileLockedCard side="seller" missingFields={sellerMissingFields} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Buyer Demand"
        subtitle="Open direct offers from buyers that your inventory could fill."
        pinTo="/opportunities"
      />

      <QueryBoundary
        query={oppQuery}
        errorTitle="Couldn't load market demand."
        empty={() => false}
      >
        {(data) => (
          <>
            {data.optedOut && (
              <Card className="text-center">
                <p className="text-sm font-medium text-foreground">
                  You&rsquo;ve opted out of unsolicited direct offers
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Turn this on in your seller profile to see buyer direct offers your inventory could fill.
                </p>
                <Link
                  to="/profile"
                  className="mt-4 inline-flex rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
                >
                  Go to profile
                </Link>
              </Card>
            )}

            {!data.optedOut && data.data.length === 0 && (
              <Card className="text-center text-sm text-muted-foreground">
                No open direct offers currently match your inventory.
              </Card>
            )}

            {!data.optedOut && data.data.length > 0 && (
              <div className="overflow-hidden rounded-lg border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                      <th className="px-4 py-3">SKU</th>
                      <th className="px-4 py-3">Condition</th>
                      <th className="px-4 py-3">Region</th>
                      <th className="px-4 py-3 text-right">Price/unit</th>
                      <th className="px-4 py-3 text-right">Qty desired</th>
                      <th className="px-4 py-3 text-right">Qty filled</th>
                      <th className="px-4 py-3">Delivery window</th>
                      <th className="px-4 py-3">Posted</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.data.map((o) => (
                      <tr key={o.id} className="hover:bg-muted">
                        <td className="px-4 py-3 font-mono text-xs text-foreground">{o.skuId.slice(0, 8)}</td>
                        <td className="px-4 py-3 text-foreground">{conditionLabel(o.conditionBand)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{o.regionId ?? 'Any'}</td>
                        <td className="px-4 py-3 text-right font-medium text-foreground">
                          ${o.pricePerUnitUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-right text-foreground">{o.quantityDesired}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{o.quantityFilled}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDeliveryWindow(o.deliverFrom, o.deliverBy)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(o.createdAt)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => setViewTarget(o)}
                              className="text-xs font-medium text-muted-foreground hover:text-foreground"
                            >
                              View units
                            </button>
                            <button
                              type="button"
                              onClick={() => setAcceptTarget(o)}
                              className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                            >
                              Accept
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </QueryBoundary>

      {acceptTarget && <AcceptDialog opportunity={acceptTarget} onClose={() => setAcceptTarget(null)} />}
      {viewTarget && <ViewUnitsDialog opportunity={viewTarget} onClose={() => setViewTarget(null)} />}
    </div>
  );
}
